import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchCommunityResources } from '@/store/slices/residents/homeSlice';
import { fetchWifiInfo } from '@/store/slices/residents/wifiSlice';
import { CommunityResource } from '@/types/residents/communityResource';
import { homeStrings } from '@/constants/residents/strings';

export interface UseResourcesScreenReturn {
  resources: CommunityResource[];
  wifiInfo: any;
  isLoading: boolean;
  refreshing: boolean;

  propertyName: string;
  unitName: string;
  subtitle: string;
  hasHandbookUrl: boolean;
  handbookUrl: string | undefined;
  resourceCount: number;

  handleRefresh: () => Promise<void>;
  handleResourcePress: (resource?: CommunityResource) => void;
  handleContactOfficePress: () => void;
  handleResidentHandbookPress: () => void;
}

export const useResourcesScreen = (): UseResourcesScreenReturn => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const { resources, isLoading } = useAppSelector((state) => state.home);
  const { wifiInfo } = useAppSelector((state) => state.wifi);
  const { user } = useAppSelector((state) => state.auth);

  const propertyId = user?.propertyId;
  const leaseId = user?.leaseId;
  const property = user?.property;

  const propertyName = property?.marketingName || property?.legalName || '';
  const unitName = user?.unitNumber ? `Unit ${user.unitNumber}` : '';

  const subtitle = useMemo(() => {
    const parts = [propertyName, unitName].filter(Boolean);
    return parts.join(' \u00B7 ');
  }, [propertyName, unitName]);

  const handbookUrl = property?.faqUrl || property?.websites?.handbook;
  const hasHandbookUrl = !!handbookUrl;

  const resourceList = useMemo(() => resources || [], [resources]);
  const resourceCount = resourceList.length;

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (propertyId) {
      dispatch(fetchCommunityResources(propertyId));
    }
    if (leaseId) {
      dispatch(fetchWifiInfo(leaseId));
    }
  }, [dispatch, propertyId, leaseId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const promises: Promise<unknown>[] = [];
      if (propertyId) {
        promises.push(dispatch(fetchCommunityResources(propertyId)));
      }
      if (leaseId) {
        promises.push(dispatch(fetchWifiInfo(leaseId)));
      }
      await Promise.all(promises);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, propertyId, leaseId]);

  const handleResourcePress = useCallback((resource?: CommunityResource) => {
    if (resource) {
      navigation.navigate('MobileResourceDetail', { resource });
    }
  }, [navigation]);

  const handleContactOfficePress = useCallback(() => {
    navigation.navigate('MobileContactOffice');
  }, [navigation]);

  const handleResidentHandbookPress = useCallback(() => {
    if (handbookUrl) {
      navigation.navigate('WebView', {
        url: handbookUrl,
        title: homeStrings.faqs.title,
      });
    }
  }, [navigation, handbookUrl]);

  return {
    resources: resourceList,
    wifiInfo,
    isLoading,
    refreshing,

    propertyName,
    unitName,
    subtitle,
    hasHandbookUrl,
    handbookUrl,
    resourceCount,

    handleRefresh,
    handleResourcePress,
    handleContactOfficePress,
    handleResidentHandbookPress,
  };
};

export default useResourcesScreen;
